/**
 * Loads the home screen widgets a few at a time instead of all at once.
 *
 * The home screen has about fifteen widgets, and each fetches its own data when the page
 * mounts. Fired together, they asked the server for more database connections than a small
 * pooler allows (Supabase session mode: 15 clients in total), and widgets failed with
 * EMAXCONNSESSION. Every home widget request goes through this queue, which runs at most
 * HOME_REQUEST_CONCURRENCY at once and starts the rest in the order they were queued. Hooks
 * mount in page order, so the widgets at the top of the page load first.
 *
 * A failed request does not stall the queue: the next one starts either way.
 */
export const HOME_REQUEST_CONCURRENCY = 3;

type Job = () => void;

let running = 0;
const waiting: Job[] = [];

function startNext(): void {
  while (running < HOME_REQUEST_CONCURRENCY && waiting.length > 0) {
    const job = waiting.shift()!;
    running += 1;
    job();
  }
}

/** Run `request` when a slot is free; resolves or rejects with its result. */
export function queueHomeRequest<T>(request: () => Promise<T>): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    waiting.push(() => {
      let result: Promise<T>;
      try {
        result = request();
      } catch (error) {
        result = Promise.reject(error);
      }
      result.then(resolve, reject).finally(() => {
        running -= 1;
        startNext();
      });
    });
    startNext();
  });
}

/** Test hook: how many requests are running and waiting right now. */
export function homeRequestQueueState(): { running: number; waiting: number } {
  return { running, waiting: waiting.length };
}
